(ns ui.components.input.integer
  (:require
   [clojure.string :as string]
   [common.number-format :as number-format]
   [common.util :as cu]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.components.input.number :as number-input]
   [ui.constants :as constants]
   [ui.dom-globals :as dg]
   [ui.util :as uu]))

(defonce digits ["0" "1" "2" "3" "4" "5" "6" "7" "8" "9"])
(defonce digits-and-minus (concat digits ["-"]))

(defonce multiplier-chars ["m" "k"])

(defonce valid-last-chars (concat digits-and-minus multiplier-chars))

(defn parse-meta
  [str-value number-format NaN]
  (let [thousand-separator (if number-format
                             (number-format/thousand-separator number-format)
                             constants/thousand-separator)
        no-thousands (if (nil? thousand-separator)
                       str-value
                       (string/replace str-value thousand-separator ""))
        lc-value (cu/str-trim-lcase no-thousands)
        len (count lc-value)
        last-char-lc (str (last lc-value))
        first-char (str (first lc-value))
        valid-first-char? (some #{first-char} digits-and-minus)
        invalid-multiplier? (not (some #{last-char-lc} valid-last-chars))
        multiplier (case last-char-lc
                     "k" 1000
                     "m" 1000000
                     1)]
    (cond (not (< 0 len constants/MAX_INTEGER_CHAR_LENGTH))
          NaN
          invalid-multiplier?
          NaN
          (not valid-first-char?)
          NaN
          :else
          (let [non-digits? (->> no-thousands
                                 (drop 1)
                                 (butlast)
                                 (remove (fn [c]
                                           (some #{c} digits)))
                                 (seq))
                valid-int-str (string/replace lc-value #"[^-0-9]" "")
                int-value (js/parseInt valid-int-str)
                int-value (cond (js/isNaN int-value)
                                NaN
                                (= 1 multiplier)
                                int-value
                                :else
                                (* int-value multiplier))
                neg? (= first-char "-")
                valid-int? (and (number? int-value)
                                (<= constants/MIN-SAFE-INTEGER int-value constants/MAX-SAFE-INTEGER))]
            (cond non-digits?
                  NaN
                  (and neg? (= len 1))
                  {:negative?    true
                   :parsed-value nil}
                  (not valid-int?)
                  NaN
                  :else
                  {:negative?    (true? neg?)
                   :parsed-value int-value})))))

(defn parse-value
  [str-value number-format]
  (let [parsed-meta (parse-meta str-value number-format nil)]
    (:parsed-value parsed-meta)))

(defn view
  [{:keys [_id _value _multiplier _on-change _auto-focus? _no-thousand-separator? _variant
           _placeholder _full-width? _number-format
           _transparent? _disabled? _on-focus _on-key-down _class _on-blur _label]}]
  (let [state-atom (r/atom {})]
    (fn [{:keys [id value multiplier on-change auto-focus? no-thousand-separator? variant
                 placeholder full-width? number-format
                 transparent? disabled? on-focus on-key-down class on-blur label]}]

      (let [{:keys [focused? temp-value selection-start selection-end]} @state-atom
            multiplier (or multiplier "")
            formatted-value (if no-thousand-separator?
                              (str value)
                              (number-input/format-value
                               {:focused?      focused?
                                :min-decimals  0
                                :max-decimals  0
                                :multiplier    multiplier
                                :number-format number-format}
                               value))
            edit-negative? (get @state-atom :negative?)
            edit-value (or temp-value
                           (when edit-negative? "-"))
            [thousand-separator] (if (nil? number-format)
                                   [constants/thousand-separator constants/decimal-separator]
                                   (number-format/separators-as-strings number-format))
            display-str (cond (not focused?)
                              formatted-value
                              (= edit-value "-")
                              (if edit-negative?
                                edit-value
                                "")
                              (and edit-value
                                   no-thousand-separator?)
                              (str edit-value)
                              edit-value
                              (uu/format-with-separators number-format
                                                         edit-value
                                                         0
                                                         edit-negative?
                                                         0)
                              :else
                              "")]
        [:input
         (merge
          {:type        "text"
           :placeholder (or placeholder "")
           :class (uu/cm "peer px-3 rounded placeholder-transparent"
                         "focus:outline-none focus:ring-0 text-md"
                         "text-left"
                         (when-not (cu/blank-value? label)
                           "")
                         (if transparent?
                           "bg-transparent disabled:bg-transparent"
                           "text-input-light-text disabled:text-disabled-regular-text
                                  bg-input-light disabled:bg-disabled-regular")
                         (when full-width?
                           "py-2 w-full")
                         (if (not= variant :borderless)
                           "py-2 border border-input-light-border-paler hover:border-input-light-border focus:border-input-light-border"
                           "py-2 border-none focus:border-transparent")
                         (when-not (cu/blank-value? value)
                           "value")
                         class)

           :ref         (fn [input-ref]
                          (when input-ref
                            ;(js/console.log "Integer input-ref " (type input-ref) " << " input-ref)
                            (when (:focused? @state-atom)
                              (oops/ocall input-ref "setSelectionRange" selection-start selection-end))
                            (when-not (:initialized? @state-atom)
                              (swap! state-atom assoc :initialized? true)
                              (when auto-focus?
                                (oops/ocall input-ref "focus")))))
           :value       (or display-str "")
           :on-focus    (fn [_e]
                          (swap! state-atom assoc :focused? true)
                          (when value
                            (let [negative? (neg-int? value)]
                              (swap! state-atom assoc
                                     :temp-value value
                                     :negative? negative?)))
                          (when (fn? on-focus)
                            (on-focus)))

           :on-blur     (fn [_e]
                          (when (fn? on-blur)
                            (if (int? temp-value)
                              (on-blur temp-value)
                              (on-blur nil)))
                          (swap! state-atom dissoc
                                 :focused?
                                 :temp-value
                                 :negative?
                                 :cleared?))

           :on-key-down (fn [e]
                          (when (fn? on-key-down)
                            (on-key-down e)))
           :on-change   (fn [e]
                          (let [value-str (dg/event-target-value e)
                                parsed-meta (if (cu/empty-value? value-str)
                                              {:negative?    false
                                               :parsed-value nil}
                                              (parse-meta value-str number-format nil))
                                {:keys [negative? parsed-value]} parsed-meta
                                formatted-value (if (or no-thousand-separator?
                                                        (= thousand-separator ""))
                                                  parsed-value
                                                  (uu/format-with-separators number-format
                                                                             parsed-value
                                                                             0
                                                                             edit-negative?
                                                                             0))
                                value-separator-count (->> value-str
                                                           (filter (fn [c]
                                                                     (= (str c) thousand-separator)))
                                                           (count))
                                new-separator-count (->> formatted-value
                                                         (str)
                                                         (filter (fn [c]
                                                                   (= (str c) thousand-separator)))
                                                         (count))
                                target (dg/target e)]
                            (when parsed-meta
                              (swap! state-atom assoc
                                     :selection-start (+ (oops/oget target "selectionStart")
                                                         (- new-separator-count value-separator-count))
                                     :selection-end (+ (oops/oget target "selectionEnd")
                                                       (- new-separator-count value-separator-count))
                                     :negative? negative?
                                     :temp-value parsed-value
                                     :cleared? (empty? value-str))
                              (when (fn? on-change)
                                (on-change parsed-value)))))}
          (when id
            {:id id})
          (when disabled?
            {:disabled true}))]))))
